import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ScrollText, Plus, Edit3, Trash2, Users, RefreshCw, Loader2, X,
  Check, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";
import { Consents } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";

/**
 * AdminConsents — CRUD for consent documents (RODO, Regulamin, custom kinds).
 *
 * Layout:
 *   Left column  = list of "kinds" (with current version summary).
 *   Right column = editor for the selected kind: publish new version OR
 *                  edit the currently published one; view historical
 *                  versions + who accepted each.
 */
export default function AdminConsents() {
  const [rows, setRows] = useState({ documents: [], by_kind: {} });
  const [kinds, setKinds] = useState([]);
  const [selectedKind, setSelectedKind] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewKind, setShowNewKind] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [all, k] = await Promise.all([
        Consents.adminList(),
        Consents.adminKinds(),
      ]);
      setRows(all);
      setKinds(k.kinds || []);
      if (!selectedKind && (k.kinds || []).length > 0) {
        setSelectedKind(k.kinds[0].kind);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie mogę pobrać zgód");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kindVersions = selectedKind ? (rows.by_kind[selectedKind] || []) : [];
  const currentDoc = kindVersions.find((d) => d.is_current) || null;

  return (
    <div className="p-3 md:p-6 space-y-4" data-testid="admin-consents-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          // Panel admina · Zgody i regulaminy
        </div>
        <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mt-1">
          Zgody i regulaminy
        </h1>
        <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
          Zarządzaj treścią dokumentów, które użytkownik musi zaakceptować.
          Publikacja nowej wersji z zaznaczonym „Wymaga re-akceptacji&rdquo; zmusi
          wszystkich do ponownego potwierdzenia po zalogowaniu.
        </p>
      </div>

      {loading ? (
        <div className="p-8 flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Ładuję dokumenty…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          {/* LEFT: kind selector */}
          <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-zinc-500" />
              <div className="text-xs font-bold text-zinc-800 uppercase tracking-widest">
                Rodzaje
              </div>
              <button
                onClick={load}
                data-testid="btn-reload-consents"
                className="ml-auto p-1 rounded hover:bg-zinc-100 text-zinc-500"
                title="Odśwież"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="divide-y divide-zinc-100">
              {kinds.map((k) => (
                <button
                  key={k.kind}
                  onClick={() => setSelectedKind(k.kind)}
                  data-testid={`kind-btn-${k.kind}`}
                  className={`w-full text-left px-3 py-2.5 flex flex-col gap-1 transition-colors ${
                    selectedKind === k.kind
                      ? "bg-zinc-900 text-white"
                      : "hover:bg-zinc-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">
                      // {k.kind}
                    </span>
                    <span className="ml-auto text-[10px] font-mono opacity-70">
                      v{k.version}
                    </span>
                  </div>
                  <div className="text-sm font-semibold truncate">{k.title}</div>
                  <div className="text-[10px] font-mono flex items-center gap-2 opacity-70">
                    <Users className="w-3 h-3" />
                    {k.accepted_count} akceptacji
                  </div>
                </button>
              ))}
              <button
                onClick={() => setShowNewKind(true)}
                data-testid="btn-new-kind"
                className="w-full text-left px-3 py-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-50"
              >
                <Plus className="w-3.5 h-3.5" /> Dodaj nowy rodzaj
              </button>
            </div>
          </div>

          {/* RIGHT: editor / history */}
          {selectedKind ? (
            <KindEditor
              kind={selectedKind}
              currentDoc={currentDoc}
              versions={kindVersions}
              onSaved={load}
            />
          ) : (
            <div className="rounded-2xl bg-white ring-1 ring-zinc-200 p-8 text-center text-sm text-zinc-500">
              Wybierz rodzaj z listy po lewej lub dodaj nowy.
            </div>
          )}
        </div>
      )}

      {showNewKind && (
        <NewKindModal
          onCancel={() => setShowNewKind(false)}
          onSaved={(kind) => {
            setShowNewKind(false);
            setSelectedKind(kind);
            load();
          }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Editor for one kind: shows current version + form to publish a new one
// and a collapsible history of previous versions with acceptance stats.
// ═════════════════════════════════════════════════════════════════════════
function KindEditor({ kind, currentDoc, versions, onSaved }) {
  return (
    <div className="space-y-4">
      {currentDoc ? (
        <CurrentDocPanel doc={currentDoc} onSaved={onSaved} />
      ) : (
        <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-6 text-amber-900 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div>Brak opublikowanej wersji dla &bdquo;{kind}&rdquo;. Opublikuj pierwszą wersję poniżej.</div>
        </div>
      )}
      <PublishNewVersion kind={kind} onSaved={onSaved} />
      <VersionHistory kind={kind} versions={versions} onChanged={onSaved} />
    </div>
  );
}

function CurrentDocPanel({ doc, onSaved }) {
  const [title, setTitle] = useState(doc.title || "");
  const [body, setBody] = useState(doc.body || "");
  const [requireReaccept, setRequireReaccept] = useState(!!doc.require_reaccept);
  const [saving, setSaving] = useState(false);

  const dirty =
    title.trim() !== (doc.title || "").trim() ||
    body !== (doc.body || "") ||
    requireReaccept !== !!doc.require_reaccept;

  const save = async () => {
    setSaving(true);
    try {
      await Consents.adminEdit(doc.id, {
        title,
        body,
        require_reaccept: requireReaccept,
      });
      toast.success("Zapisano (bez zmiany numeru wersji)");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="current-doc-panel"
      className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden"
    >
      <div className="px-4 py-2 border-b border-zinc-100 bg-emerald-50 flex items-center gap-2">
        <Check className="w-4 h-4 text-emerald-700" />
        <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">
          Aktualnie opublikowana wersja
        </div>
        <div className="text-[10px] font-mono text-emerald-700 ml-auto">
          v{doc.version} · {new Date(doc.created_at).toLocaleString("pl-PL")}
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
            Tytuł
          </label>
          <input
            data-testid="edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
            Treść
          </label>
          <textarea
            data-testid="edit-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm font-mono leading-relaxed whitespace-pre-wrap"
          />
          <div className="text-[10px] font-mono text-zinc-400 mt-1">
            Zachowuj puste linie, &bdquo;§&rdquo; i &bdquo;•&rdquo; — wyświetlą się dokładnie tak samo.
          </div>
        </div>
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            data-testid="edit-require-reaccept"
            type="checkbox"
            checked={requireReaccept}
            onChange={(e) => setRequireReaccept(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-orange-500"
          />
          <span className="text-xs text-zinc-700">
            <span className="font-bold">Wymaga ponownej akceptacji</span> —
            po zapisaniu tej flagi każdy użytkownik będzie zmuszony
            zaakceptować dokument przy następnym logowaniu.
          </span>
        </label>
        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="btn-save-current"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors ${
              dirty
                ? "bg-zinc-900 text-white hover:bg-zinc-700"
                : "bg-zinc-100 text-zinc-400 cursor-not-allowed"
            }`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
            Zapisz zmiany (bez nowej wersji)
          </button>
          <span className="text-[10px] font-mono text-zinc-400">
            // Poprawka literówki — użytkownicy NIE będą musieli akceptować ponownie.
          </span>
        </div>
      </div>
    </div>
  );
}

function PublishNewVersion({ kind, onSaved }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [requireReaccept, setRequireReaccept] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const publish = async () => {
    if (!body.trim()) {
      toast.error("Wpisz treść dokumentu");
      return;
    }
    setSaving(true);
    try {
      await Consents.adminPublish({
        kind,
        title,
        body,
        require_reaccept: requireReaccept,
      });
      toast.success("Nowa wersja opublikowana");
      setTitle("");
      setBody("");
      setRequireReaccept(true);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd publikacji");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="publish-new-panel"
      className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="btn-toggle-publish"
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-zinc-50"
      >
        <Plus className="w-4 h-4 text-orange-600" />
        <div className="text-xs font-bold uppercase tracking-widest text-zinc-800">
          Opublikuj nową wersję (&bdquo;{kind}&rdquo;)
        </div>
        {open ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
      </button>
      {open && (
        <div className="p-4 border-t border-zinc-100 space-y-3">
          <input
            data-testid="new-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Tytuł (opcjonalnie)"
            className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm"
          />
          <textarea
            data-testid="new-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            placeholder="Wpisz treść dokumentu…"
            className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm font-mono leading-relaxed"
          />
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              data-testid="new-require-reaccept"
              type="checkbox"
              checked={requireReaccept}
              onChange={(e) => setRequireReaccept(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-orange-500"
            />
            <span className="text-xs text-zinc-700">
              <span className="font-bold">Wymaga ponownej akceptacji</span> —
              każdy użytkownik zaakceptuje ponownie przy następnym logowaniu.
              Zostaw zaznaczone jeśli treść zmieniła się merytorycznie.
            </span>
          </label>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={publish}
              disabled={saving}
              data-testid="btn-publish-version"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest bg-orange-500 hover:bg-orange-600 text-white transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Publikuj wersję
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VersionHistory({ versions, onChanged }) {
  const { confirm } = useConfirm();
  const [expanded, setExpanded] = useState(false);
  const older = versions.filter((v) => !v.is_current);

  const removeVersion = async (doc) => {
    if (!(await confirm(
      `Usunąć wersję v${doc.version} „${doc.title}"? Akcja jest miękka — akceptacje pozostają w historii.`,
      { tone: "warn", confirmLabel: "Usuń" },
    ))) return;
    try {
      await Consents.adminDelete(doc.id);
      toast.success("Usunięto wersję");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd usuwania");
    }
  };

  if (older.length === 0) return null;
  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="btn-toggle-history"
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-zinc-50"
      >
        <ScrollText className="w-4 h-4 text-zinc-500" />
        <div className="text-xs font-bold uppercase tracking-widest text-zinc-800">
          Historia wersji ({older.length})
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
      </button>
      {expanded && (
        <div className="border-t border-zinc-100 divide-y divide-zinc-100">
          {older.map((d) => (
            <div key={d.id} className="p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  v{d.version} · {new Date(d.created_at).toLocaleString("pl-PL")}
                </div>
                <div className="text-sm font-semibold truncate">{d.title}</div>
                <div className="text-xs text-zinc-500 whitespace-pre-wrap line-clamp-3">
                  {d.body}
                </div>
              </div>
              <button
                onClick={() => removeVersion(d)}
                data-testid={`btn-delete-${d.id}`}
                className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                title="Usuń wersję"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewKindModal({ onCancel, onSaved }) {
  const [kind, setKind] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [requireReaccept, setRequireReaccept] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const cleanKind = (kind || "").trim();
    if (!cleanKind) return toast.error('Podaj identyfikator rodzaju (np. „cookies")');
    if (!body.trim()) return toast.error("Wpisz treść dokumentu");
    setSaving(true);
    try {
      const r = await Consents.adminPublish({
        kind: cleanKind,
        title,
        body,
        require_reaccept: requireReaccept,
      });
      toast.success("Nowy rodzaj utworzony");
      onSaved?.(r.document.kind);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-3"
      onClick={onCancel}
      data-testid="new-kind-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
      >
        <div className="p-5 border-b border-zinc-100 flex items-center gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              // Nowy rodzaj dokumentu
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight text-xl mt-1">
              Utwórz zgodę
            </h2>
          </div>
          <button
            onClick={onCancel}
            data-testid="btn-close-new-kind"
            className="ml-auto p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
              Identyfikator (slug)
            </label>
            <input
              data-testid="new-kind-slug"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              placeholder="np. cookies, marketing, poufnosc"
              className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm"
            />
            <div className="text-[10px] font-mono text-zinc-400 mt-1">
              Krótka nazwa techniczna. Znaki inne niż litery/cyfry zostaną zamienione na &bdquo;-&rdquo;.
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
              Tytuł
            </label>
            <input
              data-testid="new-kind-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="np. Polityka plików cookies"
              className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
              Treść
            </label>
            <textarea
              data-testid="new-kind-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm font-mono"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={requireReaccept}
              onChange={(e) => setRequireReaccept(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-orange-500"
            />
            <span className="text-xs text-zinc-700">
              Wymaga ponownej akceptacji przez wszystkich użytkowników.
            </span>
          </label>
        </div>
        <div className="p-4 border-t border-zinc-100 bg-zinc-50 flex items-center gap-2">
          <button
            onClick={onCancel}
            className="ml-auto px-4 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-xs font-bold uppercase tracking-widest"
          >
            Anuluj
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="btn-save-new-kind"
            className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Utwórz
          </button>
        </div>
      </div>
    </div>
  );
}
